(async () => {
const { 
  getContext,
  getElement,
  store,
  useState,
  useEffect,
  withScope
} = await import("@wordpress/interactivity");

store("gallery/scroller", {
  actions: {
    /**
     * Handles navigation between gallery items.
     * @param {PointerEvent} event 
     * @returns 
     */
    navigate(event) {
      const target = event.target;
      const classList = target.classList;
      if (!classList.contains("scroll-button") && !classList.contains("scroll-marker")) {
        return;
      }
      const context = getContext();
      let { currentIndex: index, items } = context;

      if (classList.contains("scroll-button-left")) {
        index = Math.max(0, index - 1);
      } else if (classList.contains("scroll-button-right")) {
        index = Math.min(items.length - 1, index + 1);
      } else if (classList.contains("scroll-marker")) {
        index = Array.from(target.parentElement.children).indexOf(target);
      }

      // Update context.
      context.currentIndex = index;
    },
    /**
     * Handles endscroll event to update current index.
     * @param {Event} event 
     */
    endscroll(event) {
      const { ref } = getElement();
      const context = getContext();
      const firstSnapped = ref.querySelector(":scope > .\\@scroll-snapped-x");
      context.currentIndex = context.items?.indexOf(firstSnapped);
    }
  },
  // Automatically reacts to state changes.
  callbacks: {
    scroll() {
      if (CSS.supports(`selector(::scroll-marker-group)`)) {
        return;
      }

      useEffect( () => {
        const { ref } = getElement();
        const context = getContext();

        const observer = new IntersectionObserver(([entry]) => {
          entry.target.classList.toggle("@scroll-snapped-x", entry.isIntersecting);
        }, {
          root: ref,
          threshold: 1.0,
        });

        const items = context.items = Array.from(ref.children, (child) => {
          observer.observe(child);
          return child;
        });
        context.currentIndex = 0;

        ref.insertAdjacentHTML("afterbegin", `
        <button 
          class="scroll-button scroll-button-left" 
          aria-label="Go to previous item"
          data-wp-on--click="actions.navigate"
        >‹</button>
        <button 
          class="scroll-button scroll-button-right" 
          aria-label="Go to next item"
          data-wp-on--click="actions.navigate"
        >›</button>
        <nav class="scroll-marker-group">
          ${items.map((child, index) => `
            <button 
              class="scroll-marker" 
              aria-label="Go to item ${index + 1}"
              data-wp-on--click="actions.navigate"
            ></button>
          `).join("")}
        </nav>   
        `);

        // Store controls in context for easy access.
        context.scrollButtonLeft = ref.querySelector(".scroll-button-left");
        context.scrollButtonRight = ref.querySelector(".scroll-button-right");
        context.scrollMarkerGroup = ref.querySelector(".scroll-marker-group");
      });
    },
    // Updates controls based on current index change.
    update() {
      const { 
        currentIndex, items, 
        scrollButtonLeft, scrollButtonRight,
        scrollMarkerGroup,
      } = getContext();

      items?.[currentIndex]?.scrollIntoView({ 
        block: "nearest", inline: "center" 
      });

      scrollButtonLeft && (scrollButtonLeft.disabled = currentIndex === 0);
      scrollButtonRight && (scrollButtonRight.disabled = currentIndex === items.length - 1);
      if (scrollMarkerGroup) {
        const markers = scrollMarkerGroup.children;
        for (const marker of markers) {
          const index = Array.from(markers).indexOf(marker);
          marker.setAttribute("aria-current", `${index === currentIndex}`);
        }
      }
    },
  },
});
})();